/**
 * Form values and rules of the element dialog, plus the list helpers of the
 * elements screen.
 */
import { getImage, getStorage, STORAGE } from '@/shared/lib/image';
import { sortByKey } from '@/shared/lib/sort';
import { z } from 'zod';
import type { DmsItem } from '../api';
import {
  displayAvailability,
  numberKey,
  textKey,
  type ListSort,
} from '../list';

/** `data` of an element item; the fields the iPad app adds are kept (round trip). */
export const elementDataSchema = z
  .object({
    type: z.string().nullish(),
    name: z.string().nullish(),
    codeBu: z.string().nullish(),
    buttonStyleCodePart: z.string().nullish(),
    shirtButtonStyleCodePart: z.string().nullish(),
    liningStyleCodePart: z.string().nullish(),
    description: z.string().nullish(),
    order: z.union([z.number(), z.string()]).nullish(),
    availablePermanent: z.boolean().nullish(),
    availableVillaDior: z.boolean().nullish(),
    picture: z.string().nullish(),
    /** The sketch layer the front office draws over the main picture ("black" picture type). */
    blackPicture: z.string().nullish(),
    mainPictureUpdatedAt: z.number().nullish(),
    blackPictureUpdatedAt: z.number().nullish(),
  })
  .loose();
export type ElementData = z.infer<typeof elementDataSchema>;

export type DmsElement = DmsItem<ElementData>;

export interface ElementFormValues {
  type: string;
  name: string;
  codeBu: string;
  buttonStyleCodePart: string;
  shirtButtonStyleCodePart: string;
  liningStyleCodePart: string;
  description: string;
  order: string;
  availablePermanent: boolean;
  availableVillaDior: boolean;
}

export type ElementFormErrors = Partial<
  Record<keyof ElementFormValues, string>
>;

export const elementFormValues = (
  item: DmsElement | null,
  type: string | null,
): ElementFormValues => ({
  type: item?.data.type ?? type ?? '',
  name: item?.data.name ?? '',
  codeBu: item?.data.codeBu ?? '',
  buttonStyleCodePart: item?.data.buttonStyleCodePart ?? '',
  shirtButtonStyleCodePart: item?.data.shirtButtonStyleCodePart ?? '',
  liningStyleCodePart: item?.data.liningStyleCodePart ?? '',
  description: item?.data.description ?? '',
  order: item?.data.order == null ? '' : String(item.data.order),
  availablePermanent: !!item?.data.availablePermanent,
  availableVillaDior: !!item?.data.availableVillaDior,
});

/** The code part field enabled for a type, if any. */
export const codePartField = (
  type: string,
):
  | 'buttonStyleCodePart'
  | 'shirtButtonStyleCodePart'
  | 'liningStyleCodePart'
  | null => CODE_PART_FIELDS[type] ?? null;

const CODE_PART_FIELDS: Record<
  string,
  'buttonStyleCodePart' | 'shirtButtonStyleCodePart' | 'liningStyleCodePart'
> = {
  BUTTON: 'buttonStyleCodePart',
  'SHIRT BUTTON': 'shirtButtonStyleCodePart',
  LINING: 'liningStyleCodePart',
};

const CODE_PART_PATTERN = /^[a-zA-Z0-9]$/;

export const CODE_PART_MESSAGES = {
  buttonStyleCodePart: 'Wrong Button Code',
  shirtButtonStyleCodePart: 'Wrong Shirt Button Code',
  liningStyleCodePart: 'Wrong Lining Code',
} as const;

export const validateElementForm = (
  values: ElementFormValues,
): ElementFormErrors => {
  const errors: ElementFormErrors = {};
  if (!values.type) errors.type = 'Wrong type';
  if (!values.name.trim()) errors.name = 'Wrong name';
  if (!values.codeBu.trim()) errors.codeBu = 'Wrong Code BU';
  if (!values.description.trim()) errors.description = 'Wrong description';
  const order = values.order.trim() === '' ? Number.NaN : Number(values.order);
  if (Number.isNaN(order) || order < 0) errors.order = 'Wrong order';
  const codePart = codePartField(values.type);
  if (codePart && !CODE_PART_PATTERN.test(values[codePart]))
    errors[codePart] = CODE_PART_MESSAGES[codePart];
  return errors;
};

/**
 * The enabled controls only (the code parts of the
 * other types are disabled, hence absent), the order as a number, a picture
 * stamp; the picture itself is set after the upload.
 */
export const formToElementData = (
  values: ElementFormValues,
  now: number,
): ElementData => {
  const codePart = codePartField(values.type);
  const data: ElementData = {
    type: values.type,
    name: values.name,
    codeBu: values.codeBu,
    description: values.description,
    order: values.order.trim() === '' ? null : Number(values.order),
    availablePermanent: values.availablePermanent,
    availableVillaDior: values.availableVillaDior,
    mainPictureUpdatedAt: now,
    blackPictureUpdatedAt: now,
  };
  if (codePart) data[codePart] = values[codePart];
  return data;
};

/** Element types drawn with a sketch layer in the front office: both pictures are required. */
export const ELEMENT_TYPES_WITH_SKETCH: ReadonlySet<string> = new Set([
  'COLLAR',
  'BUTTONING TAB',
  'CUFF',
  'HEM',
]);
export const hasSketchPicture = (type: string): boolean =>
  ELEMENT_TYPES_WITH_SKETCH.has(type);

export const MAIN_PICTURE_SIZE = { width: 300, height: 300 } as const;
export const SKETCH_PICTURE_SIZE = { width: 2454, height: 2219 } as const;

/** The storage decides the platform of the signature. */
const storedPictureUrl = (path: string): string | null =>
  getStorage(path) === STORAGE.AbcDiorBack
    ? getImage('abc', undefined, path)
    : getImage('dms', undefined, path);

/** The stored main picture, when on a known storage; versioned by its update stamp. */
export const elementPictureUrl = (item: DmsElement | null): string | null => {
  const path = item?.data.picture;
  if (!path || !getStorage(path)) return null;
  const url = storedPictureUrl(path);
  const updatedAt = item?.data.mainPictureUpdatedAt;
  return url && updatedAt ? `${url}&v=${updatedAt}` : url;
};

/** Name of the sketch file on the bucket. */
export const sketchPictureFileName = (code: string): string =>
  `${code}_BLACK.png`;

/**
 * The stored sketch, else the conventional file
 * of the code on the DMS assets; versioned by its update stamp.
 */
export const elementSketchUrl = (item: DmsElement | null): string | null => {
  if (!item) return null;
  const { blackPicture, blackPictureUpdatedAt, codeBu } = item.data;
  let url: string | null = null;
  if (blackPicture) url = storedPictureUrl(blackPicture);
  else if (codeBu)
    url = getImage(
      'dms',
      STORAGE.DmsFrontAssets,
      `/configurator/element/${sketchPictureFileName(codeBu)}`,
    );
  return url && blackPictureUpdatedAt
    ? `${url}&v=${blackPictureUpdatedAt}`
    : url;
};

export const ELEMENT_SORT_FIELDS = [
  'type',
  'name',
  'codeBu',
  'displayAvailability',
  'order',
] as const;
export type ElementSortField = (typeof ELEMENT_SORT_FIELDS)[number];
export type ElementSort = ListSort<ElementSortField>;

/** The elements of a type (all when none), sorted. */
export const displayedElements = (
  items: readonly DmsElement[],
  type: string | null,
  sort: ElementSort,
): DmsElement[] =>
  sortByKey(
    type ? items.filter((item) => item.data.type === type) : [...items],
    (item) =>
      sort.by === 'order'
        ? numberKey(item.data.order)
        : textKey(elementSortText(item.data, sort.by)),
    sort.reverse,
  );

const elementSortText = (
  data: DmsElement['data'],
  by: Exclude<ElementSortField, 'order'>,
): unknown =>
  by === 'displayAvailability' ? displayAvailability(data) : data[by];

/** The villa dior finishings option cannot be deleted. */
export const isProtectedElement = (item: DmsElement): boolean =>
  /villa dior finishing/i.test(item.data.name ?? '');
